import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import { useApiClient } from "../../../shared/customHooks";
import { useLocation, useNavigate, useParams } from "react-router";
import { PageHeader } from "../../header/PageHeader";
import { Role } from "../../../shared/enums/Role";
import { Button } from "primereact/button";
import { useGlobalContext } from "../../../shared/GlobalContext";
import { ToastInfo } from "../../toaster/toaster";
import { eventEmitter } from "../../../shared/event";

// Create a Context
const ApplicationContext = createContext<IApplicationContext>({
  id: "",
  application: {},
  applicationBase: {},
  rehydrate: async () => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setApplication: (application: any) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setHeaderText: (headerText: IHeadetText) => {},
});

// Create a provider component
export const ApplicationContextProvider = ({ children }: any) => {
  const [application, setApplication] = useState<any>({});
  const [loaded, setLoaded] = useState(false);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const {
    accountInfo: { role },
  } = useGlobalContext();
  const { fetchData } = useApiClient(getController());

  const [headerText, setHeaderText] = useState<IHeadetText>({
    value: "Edit Type of Application",
    icon: "view-app-icon mr-3 mt-0",
  });

  const navigate = useNavigate();

  useEffect(() => {
    getApplication(id ?? "");
  }, [id]);

  function rehydrate(): Promise<void | never> {
    return getApplication(id ?? "");
  }

  function getController(): string {
    let controller = "";
    switch (role) {
      case Role.kindergarten:
        controller = "KindergartenBaseApplication";
        break;
      case Role.department:
        controller = "DepartmentApplication";
        break;
      default:
        controller = "BaseApplication";
    }
    return controller;
  }

  function getApplication(id: string): Promise<void | never> {
    let res: Promise<void | never>;
    switch (role) {
      case Role.assessor:
      case Role.manager:
      case Role.root: {
        res = fetchData(id).then(async (res) => {
          const url = res.data.applicationBase.discriminator + "/" + res.data.applicationBaseId;
          await getApplicationBase(res.data, url);
        });
        break;
      }
      case Role.provider: {
        res = fetchData(id).then(async (res) => {
          const url = "Provider" + res.data.applicationBase.discriminator + "/" + res.data.applicationBaseId;
          await getApplicationBase(res.data, url);
        });
        break;
      }
      case Role.kindergarten: {
        res = fetchData(id).then(async (res) => {
          setApplication(res.data);
          setLoaded(true);
        });
        break;
      }
      case Role.department: {
        res = fetchData(id)
          .then(async (res) => {
            setApplication(res.data);
            setLoaded(true);
          })
          .catch((error) => {
            if (error.status == 403) {
              navigate("/department/applications");
              const errorData: ToastInfo = {
                title: "Error",
                message: "You may not view presubmission applications",
                severity: "error",
              };
              eventEmitter.emit("tstr", errorData);
            }
            setLoaded(false);
          });
        break;
      }
      default: {
        res = Promise.reject(new Error("Invalid role"));
      }
    }
    return res;
  }

  async function getApplicationBase(resData: any, url: string) {
    const base = await fetchData(url);
    resData.applicationBase = base.data;
    setApplication(resData);
    setLoaded(true);
  }

  const childInfo = useMemo(() => {
    if (application.applicationBase == null) return {};
    const { child, kindergarten } = application.applicationBase;
    return {
      name: `${child?.firstName} ${child?.lastName}`.trim(),
      kinder: kindergarten?.fundedKinder.accountName == "Other" ? kindergarten?.kindergartenNameOther : kindergarten?.fundedKinder.accountName,
    };
  }, [application]);
  const printApplication = () => {
    window.print();
  };
  return (
    <div className="w-full">
      <ApplicationContext.Provider
        value={{
          id: id ?? "",
          application,
          applicationBase: application.applicationBase,
          rehydrate,
          setApplication,
          setHeaderText,
        }}>
        <PageHeader headerText={[headerText]} icon={"icon"}>
          <div className="student-info">
            {loaded && (
              <>
                <div className="flex justify-content-between ">
                  {" "}
                  <div className="flex align-items-center justify-content-center flex-wrap">
                    <span className="stu-details-icon"></span>
                    <span className="text-purple mr-1">
                      <b className="white-space-nowrap">
                        {(() => {
                          if (application.applicationNumber > 0) {
                            return application.typeOfApplication != undefined && application.typeOfApplication == 0
                              ? `Appeal ${application.applicationNumber}`
                              : `Transfer ${application.applicationNumber}`;
                          } else if (application.hasAppeal) {
                            return "Appeal Received";
                          } else if (application.hasTransfer) {
                            return "Transfer Received";
                          }
                        })()}
                      </b>
                    </span>
                    <span className="mr-1 white-space-nowrap">Application for:</span> <b className="white-space-nowrap">{childInfo?.name}</b>{" "}
                    <span className="mx-1 white-space-nowrap">at kindergarten</span>
                    <b className="white-space-nowrap">{childInfo?.kinder}</b>, <span className="mx-1 white-space-nowrap">Reference ID:</span>{" "}
                    <b>{id}</b>
                  </div>
                  {(location.pathname.indexOf("ApplicationBase") > 0 && location.pathname.split("/").length == 4) ||
                  location.pathname.match(/^\/provider\/applications\/ApplicationBaseCurrent\/(\d+)$/) ||
                  location.pathname.match(/^\/presubmission\/(\d+)$/) ||
                  location.pathname.match(/^\/kindergarten\/pendingApplications\/(\d+)$/) ||
                  location.pathname.match(/^\/kindergarten\/submittedApplications\/(\d+)$/) ? (
                    <Button
                      icon="pi pi-print"
                      className="filled-round-button ml-2 float-right dont-show-in-print white-space-nowrap"
                      onClick={printApplication}
                      label="Print Application"
                    />
                  ) : (
                    ""
                  )}
                </div>
              </>
            )}
          </div>
        </PageHeader>
        {loaded && children}
      </ApplicationContext.Provider>
    </div>
  );
};

interface IApplicationContext {
  id: string;
  application: any;
  applicationBase: any;
  rehydrate: () => Promise<void | never>;
  setApplication: (application: any) => void;
  setHeaderText: (headerText: IHeadetText) => void;
}

interface IHeadetText {
  value: string;
  icon: string;
  class?: string;
}
// Custom hook for using the context
export const useApplicationContext = (): IApplicationContext => useContext(ApplicationContext);
